import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Modal, Form, Input, Switch, Select, message, Popconfirm, Space } from 'antd';
import { transportModeApi, type TransportModeDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

const ALL_MODES = [
  { label: '空运 (AIR)', value: 'AIR' },
  { label: '海运 (SEA)', value: 'SEA' },
  { label: '快递 (EXPRESS)', value: 'EXPRESS' },
  { label: '铁路 (RAIL)', value: 'RAIL' },
  { label: '卡车 (TRUCK)', value: 'TRUCK' },
  { label: '卡航 (TRUCK_AIR)', value: 'TRUCK_AIR' },
];

const TransportModeManage: React.FC = () => {
  const [list, setList] = useState<TransportModeDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TransportModeDTO | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    transportModeApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载运输方式');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const usedModes = useMemo(() => new Set(list.map((t) => t.mode)), [list]);

  const modeOptions = useMemo(() => {
    if (editing) {
      return ALL_MODES;
    }
    return ALL_MODES.filter((m) => !usedModes.has(m.value));
  }, [editing, usedModes]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const openEdit = (r: TransportModeDTO) => {
    setEditing(r);
    form.setFieldsValue({
      mode: r.mode,
      displayName: r.displayName,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload: Partial<TransportModeDTO> = {
      mode: values.mode as string,
      displayName: values.displayName as string,
      isActive: values.isActive as boolean,
    };
    const submit = editing?.id
      ? transportModeApi.update(editing.id, payload)
      : transportModeApi.create(payload);
    submit
      .then(() => {
        message.success(editing ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onDelete = (id: string) => {
    transportModeApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  return (
    <>
      <Card
        title="运输方式列表"
        extra={
          <Button type="primary" onClick={openAdd} disabled={modeOptions.length === 0 && !editing}>
            新增运输方式
          </Button>
        }
      >
        <p style={{ marginBottom: 16 }}>
          编码仅可为 AIR / SEA / EXPRESS / RAIL / TRUCK / TRUCK_AIR，每种编码最多一条。承运商请在线路中选择。
        </p>
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '编码', dataIndex: 'mode', width: 120 },
            { title: '显示名称', dataIndex: 'displayName', width: 160 },
            {
              title: '启用',
              dataIndex: 'isActive',
              width: 80,
              render: (v: boolean) => (v !== false ? '是' : '否'),
            },
            {
              title: '操作',
              width: 140,
              render: (_, r) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定删除该运输方式？" onConfirm={() => onDelete(r.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editing ? `编辑运输方式：${editing.mode}` : '新增运输方式'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="mode" label="编码" rules={[{ required: true, message: '请选择编码' }]}>
            <Select options={modeOptions} disabled={!!editing} placeholder="选择运输方式编码" />
          </Form.Item>
          <Form.Item name="displayName" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TransportModeManage;
